export type Point = { x: number; y: number };

/**
 * Homografia (rzut projekcyjny) mapująca prostokąt źródłowy na czworokąt docelowy —
 * zwraca łańcuch CSS matrix3d. Standardowy algorytm 2D projective transform.
 * Używane dla renderów perspektywicznych (FE-1); en face to przypadek szczególny.
 */
function adj(m: number[]): number[] {
  return [
    m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
    m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
    m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3],
  ];
}

function multmm(a: number[], b: number[]): number[] {
  const c: number[] = [];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      let cij = 0;
      for (let k = 0; k < 3; k++) {
        cij += a[3 * i + k] * b[3 * k + j];
      }
      c[3 * i + j] = cij;
    }
  }
  return c;
}

function multmv(m: number[], v: number[]): number[] {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

function basisToPoints(p: Point[]): number[] {
  const m = [p[0].x, p[1].x, p[2].x, p[0].y, p[1].y, p[2].y, 1, 1, 1];
  const v = multmv(adj(m), [p[3].x, p[3].y, 1]);
  return multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
}

/**
 * matrix3d mapujący prostokąt [0,0]-[w,h] (rogi TL,TR,BR,BL) na docelowe 4 punkty
 * (TL,TR,BR,BL). Element musi mieć transform-origin: 0 0 i rozmiar w×h.
 */
export function quadMatrix3d(w: number, h: number, dst: Point[]): string {
  const src: Point[] = [{x: 0, y: 0}, {x: w, y: 0}, {x: w, y: h}, {x: 0, y: h}];
  const s = basisToPoints(src);
  const d = basisToPoints(dst);
  const m = multmm(d, adj(s));

  for (let i = 0; i < 9; i++) {
    m[i] /= m[8];
  }

  const t = [
    m[0], m[3], 0, m[6],
    m[1], m[4], 0, m[7],
    0, 0, 1, 0,
    m[2], m[5], 0, m[8],
  ];

  return `matrix3d(${t.join(',')})`;
}

/**
 * matrix3d mapujący DOWOLNY czworokąt `src` (4 punkty TL,TR,BR,BL) na `dst` (4 punkty).
 * Element musi mieć transform-origin: 0 0. Uogólnienie quadMatrix3d (tam src to prostokąt
 * [0,0]-[w,h]); tu src bywa podregionem (fragment F głównego zdjęcia) — AT-9336 seamless.
 */
export function quadToQuadMatrix3d(src: Point[], dst: Point[]): string {
  const s = basisToPoints(src);
  const d = basisToPoints(dst);
  const m = multmm(d, adj(s));

  for (let i = 0; i < 9; i++) {
    m[i] /= m[8];
  }

  const t = [
    m[0], m[3], 0, m[6],
    m[1], m[4], 0, m[7],
    0, 0, 1, 0,
    m[2], m[5], 0, m[8],
  ];

  return `matrix3d(${t.join(',')})`;
}

export function centroid(points: Point[]): Point {
  const n = points.length || 1;
  return {
    x: points.reduce((s, p) => s + p.x, 0) / n,
    y: points.reduce((s, p) => s + p.y, 0) / n,
  };
}

/**
 * Homografia jednostkowego kwadratu [0,1]² (rogi TL,TR,BR,BL) na docelowy czworokąt.
 * Pozwala rzutować dowolny punkt płaszczyzny ściany na współrzędne ekranu z zachowaniem
 * perspektywy — praca dziedziczy zbieżność renderu (FE-1), a nie jest płaskim równoległobokiem.
 */
export function quadHomography(dst: Point[]): number[] {
  const unit: Point[] = [{x: 0, y: 0}, {x: 1, y: 0}, {x: 1, y: 1}, {x: 0, y: 1}];
  return multmm(basisToPoints(dst), adj(basisToPoints(unit)));
}

/** Rzutuje punkt (u,v) z przestrzeni jednostkowej przez homografię na współrzędne ekranu. */
export function projectPoint(h: number[], u: number, v: number): Point {
  const r = multmv(h, [u, v, 1]);
  return {x: r[0] / r[2], y: r[1] / r[2]};
}

/** Długość euklidesowa odcinka. */
export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Czworokąt pracy osadzonej w płaszczyźnie ściany zdefiniowanej czworokątem kalibracji
 * (TL,TR,BR,BL) — rzut projekcyjny, więc praca dziedziczy perspektywę renderu. Rozmiar to ułamek
 * fizycznej rozpiętości strefy (długości krawędzi / pxPerCm), wyśrodkowany w strefie. Praca większa
 * niż strefa jest zmniejszana JEDNOLICIE (jeden współczynnik dla obu wymiarów), więc zachowuje
 * proporcje i nigdy nie wychodzi poza strefę, a więc i poza render (AT-9369). Praca mieszcząca się
 * w strefie ma swój rzeczywisty rozmiar.
 */
export function perspectiveArtworkQuad(
  quad: Point[],
  pxPerCm: number,
  dimensions: { width: number; height: number },
): Point[] {
  const [tl, tr, br, bl] = quad;
  const wallWidthCm = (distance(tl, tr) + distance(bl, br)) / 2 / pxPerCm;
  const wallHeightCm = (distance(tl, bl) + distance(tr, br)) / 2 / pxPerCm;

  const fit = Math.min(1, wallWidthCm / dimensions.width, wallHeightCm / dimensions.height);
  const halfFx = (dimensions.width * fit) / wallWidthCm / 2;
  const halfFy = (dimensions.height * fit) / wallHeightCm / 2;

  const h = quadHomography(quad);
  return [
    projectPoint(h, 0.5 - halfFx, 0.5 - halfFy), // TL
    projectPoint(h, 0.5 + halfFx, 0.5 - halfFy), // TR
    projectPoint(h, 0.5 + halfFx, 0.5 + halfFy), // BR
    projectPoint(h, 0.5 - halfFx, 0.5 + halfFy), // BL
  ];
}

/**
 * Współczynnik 0–1, o jaki trzeba zmniejszyć pracę rysowaną en face (render bez czworokąta), żeby
 * mieściła się w obrazie renderu. Środek pracy leży na `centerYRatio` wysokości obrazu, więc w pionie
 * liczy się bliższa krawędź. Jeden współczynnik dla obu wymiarów — proporcje zostają (AT-9369).
 */
export function frontalFitScale(
  artworkWidthPx: number,
  artworkHeightPx: number,
  imageWidthPx: number,
  imageHeightPx: number,
  centerYRatio: number,
): number {
  const maxHeightPx = 2 * Math.min(centerYRatio, 1 - centerYRatio) * imageHeightPx;

  return Math.min(1, imageWidthPx / artworkWidthPx, maxHeightPx / artworkHeightPx);
}
